import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState } from 'react-native';

import { useMicroSurveyGateway } from './MicroSurveyGatewayProvider';
import type { MicroSurvey } from '../model/types';

import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { useAuthResolved } from '@/hooks/useAuthResolved';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useUserScope } from '@/hooks/useUserScope';
import { getCurrentUserScopedQueryOptions } from '@/services/getCurrentUserScoped';
import {
  trackMicroSurveyClosed,
  trackMicroSurveyDisplayed,
  trackMicroSurveyResponded,
} from '@/util/analyticsEvents';
import { isEHRSession } from '@/util/smartLaunch';

/** Delay in ms before fetching a micro-survey after login. */
export const MICRO_SURVEY_DELAY_MS = 3000; // 30_000

type MicroSurveyState = {
  survey: MicroSurvey | null;
  visible: boolean;
  showThankYou: boolean;
  dismissThankYou: () => void;
  loading: boolean;
  submit: (response: string, additionalInfo?: string | null) => Promise<void>;
  close: () => Promise<void>;
};

/**
 * Manages micro-survey lifecycle: waits MICRO_SURVEY_DELAY_MS ms after auth, fetches from API once
 * per session, and provides submit/close handlers.
 *
 * Skips the survey when offline. Restarts the timer if the app goes to the background.
 */
export function useMicroSurvey(): MicroSurveyState {
  const authStateResolved = useAuthResolved();

  const { userId, canFetch } = useUserScope();

  const isOnline = useIsOnline();

  const { data: user } = useQuery(getCurrentUserScopedQueryOptions({ userId }));

  const isMicroSurveysEnabled = useFeatureFlag(FEATURE_FLAGS.MICRO_SURVEYS);

  const gateway = useMicroSurveyGateway();

  const isLmp = user?.userType === 'lmp';

  const [survey, setSurvey] = useState<MicroSurvey | null>(null);

  const [visible, setVisible] = useState(false);

  const [showThankYou, setShowThankYou] = useState(false);

  const thankYouTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [loading, setLoading] = useState(false);

  const fetchedRef = useRef(false);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);

      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();

    timerRef.current = setTimeout(async () => {
      if (fetchedRef.current) {
        return;
      }

      if (!isOnline) {
        return;
      }

      fetchedRef.current = true;

      setLoading(true);

      try {
        const data = await gateway.getNextMicroSurvey();

        if (data) {
          setSurvey(data);

          setVisible(true);

          trackMicroSurveyDisplayed(data.type);
        }
      } finally {
        setLoading(false);
      }
    }, MICRO_SURVEY_DELAY_MS);
  }, [clearTimer, isOnline, gateway]);

  useEffect(() => {
    if (
      !authStateResolved ||
      !canFetch ||
      !isLmp ||
      !isMicroSurveysEnabled ||
      !isOnline ||
      fetchedRef.current ||
      isEHRSession()
    ) {
      return;
    }

    startTimer();

    return clearTimer;
  }, [authStateResolved, canFetch, isLmp, isMicroSurveysEnabled, isOnline, startTimer, clearTimer]);

  useEffect(() => {
    const sub = AppState.addEventListener('change', (status) => {
      if (
        status === 'active' &&
        !fetchedRef.current &&
        canFetch &&
        isLmp &&
        isMicroSurveysEnabled &&
        isOnline &&
        !isEHRSession()
      ) {
        startTimer();
      } else if (status !== 'active') {
        clearTimer();
      }
    });

    return () => sub.remove();
  }, [canFetch, isLmp, isMicroSurveysEnabled, isOnline, startTimer, clearTimer]);

  const submit = useCallback(
    async (response: string, additionalInfo?: string | null) => {
      if (!survey) {
        return;
      }

      setVisible(false);

      setShowThankYou(true);

      if (thankYouTimerRef.current) {
        clearTimeout(thankYouTimerRef.current);
      }

      thankYouTimerRef.current = setTimeout(() => {
        setShowThankYou(false);
      }, 3000);

      trackMicroSurveyResponded({ surveyType: survey.type, response, additionalInfo });

      await gateway.submitMicroSurvey({
        surveyType: survey.type,
        response,
        additionalInfo: additionalInfo ?? null,
        closed: false,
      });
    },
    [survey, gateway]
  );

  const close = useCallback(async () => {
    if (!survey) {
      return;
    }

    setVisible(false);

    trackMicroSurveyClosed(survey.type);

    await gateway.submitMicroSurvey({
      surveyType: survey.type,
      closed: true,
    });
  }, [survey, gateway]);

  const dismissThankYou = useCallback(() => {
    setShowThankYou(false);

    if (thankYouTimerRef.current) {
      clearTimeout(thankYouTimerRef.current);

      thankYouTimerRef.current = null;
    }
  }, []);

  return { survey, visible, showThankYou, dismissThankYou, loading, submit, close };
}
